import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  HealthAndSafety,
  CheckCircle,
  Error,
  Warning,
  HelpOutline,
  Refresh,
  PlayArrow,
  OpenInNew,
  SystemUpdateAlt,
  Upload
} from '@mui/icons-material';
import { useAuth } from '../services/AuthContext';
import api from '../services/api';

const SiteHealth = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [checkingSiteId, setCheckingSiteId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [healthData, setHealthData] = useState({ sites: [], summary: {} });
  const [statusFilter, setStatusFilter] = useState('all');

  // Plugin update states
  const [pluginVersion, setPluginVersion] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [updatingSiteId, setUpdatingSiteId] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      fetchHealthStatus();
      fetchPluginVersion();
    }
  }, [isAdmin]);

  const fetchHealthStatus = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/admin/site-health');
      setHealthData(response.data);
    } catch (err) {
      console.error('Error fetching health status:', err);
      setError(`Failed to fetch health status: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const checkAllSites = async () => {
    try {
      setChecking(true);
      setError('');
      setSuccess('');
      const response = await api.post('/admin/site-health/check-all');
      if (response.data.success) {
        setSuccess(`Checked ${response.data.summary.healthy + response.data.summary.degraded + response.data.summary.offline} sites - Healthy: ${response.data.summary.healthy}, Degraded: ${response.data.summary.degraded}, Offline: ${response.data.summary.offline}`);
        fetchHealthStatus();
      }
    } catch (err) {
      setError(`Failed to check sites: ${err.response?.data?.detail || err.message}`);
    } finally {
      setChecking(false);
    }
  };

  const checkSingleSite = async (siteId) => {
    try {
      setCheckingSiteId(siteId);
      setError('');
      const response = await api.post(`/admin/site-health/${siteId}/check`);
      setSuccess(`${response.data.rootdomain}: ${response.data.status.toUpperCase()}`);
      fetchHealthStatus();
    } catch (err) {
      setError(`Failed to check site: ${err.response?.data?.detail || err.message}`);
    } finally {
      setCheckingSiteId(null);
    }
  };

  // Plugin update functions
  const fetchPluginVersion = async () => {
    try {
      const response = await api.get('/admin/plugin/version');
      setPluginVersion(response.data);
    } catch (err) {
      console.error('Failed to fetch plugin version:', err);
    }
  };

  const updateSingleSite = async (siteId) => {
    try {
      setUpdatingSiteId(siteId);
      setError('');
      const response = await api.post(`/admin/plugin/update-site/${siteId}`);
      if (response.data.success) {
        setSuccess(`Updated ${response.data.rootdomain}: ${response.data.previous_version} → ${response.data.new_version}`);
        fetchHealthStatus();
      } else {
        setError(`Update failed for ${response.data.rootdomain}: ${response.data.error}`);
      }
    } catch (err) {
      setError(`Failed to update site: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUpdatingSiteId(null);
    }
  };

  const updateAllSites = async () => {
    try {
      setUpdating(true);
      setError('');
      setSuccess('');
      const response = await api.post('/admin/plugin/update-all');
      if (response.data.success) {
        const { summary } = response.data;
        setSuccess(`Update complete - Updated: ${summary.updated}, Failed: ${summary.failed}, Skipped: ${summary.skipped}`);
        fetchHealthStatus();
      }
    } catch (err) {
      setError(`Failed to update sites: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUpdating(false);
    }
  };

  // Check if site needs update
  const needsUpdate = (siteVersion) => {
    if (!pluginVersion || !siteVersion) return false;
    const current = (siteVersion || '').replace(/^v/i, '');
    const latest = (pluginVersion.version || '').replace(/^v/i, '');
    return current !== latest;
  };

  // Count sites that need update
  const sitesNeedingUpdate = healthData.sites.filter(site => needsUpdate(site.plugin_version)).length;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'degraded':
        return <Warning sx={{ color: 'warning.main' }} />;
      case 'offline':
        return <Error sx={{ color: 'error.main' }} />;
      default:
        return <HelpOutline sx={{ color: 'text.secondary' }} />;
    }
  };

  const getStatusChip = (status) => {
    const config = {
      healthy: {
        color: 'success',
        label: 'Healthy',
        tooltip: 'Plugin is connected and form fetching is working correctly'
      },
      degraded: {
        color: 'warning',
        label: 'Degraded',
        tooltip: 'Plugin is connected but form fetching is failing'
      },
      offline: {
        color: 'error',
        label: 'Offline',
        tooltip: 'Plugin is not reachable - site may be down or plugin deactivated'
      },
      unknown: {
        color: 'default',
        label: 'Unknown',
        tooltip: 'No health check has been run yet for this site'
      }
    };
    const { color, label, tooltip } = config[status] || config.unknown;
    return (
      <Tooltip title={tooltip} arrow>
        <Chip size="small" color={color} label={label} icon={getStatusIcon(status)} />
      </Tooltip>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  // Filter sites by status
  const filteredSites = statusFilter === 'all'
    ? healthData.sites
    : healthData.sites.filter(site => site.status === statusFilter);

  if (!isAdmin) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          Access denied. Only administrators can access this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <HealthAndSafety sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
          <Typography variant="h4" component="h1" fontWeight="bold">
            Site Health Monitor
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Monitor plugin connectivity and form fetching status for all connected sites
        </Typography>
      </Box>

      {/* Status Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircle sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h3" fontWeight="bold">
                {healthData.summary.healthy || 0}
              </Typography>
              <Typography variant="body2">Healthy</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Warning sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h3" fontWeight="bold">
                {healthData.summary.degraded || 0}
              </Typography>
              <Typography variant="body2">Degraded</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Error sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h3" fontWeight="bold">
                {healthData.summary.offline || 0}
              </Typography>
              <Typography variant="body2">Offline</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: 'grey.300' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <HelpOutline sx={{ fontSize: 40, mb: 1, color: 'text.secondary' }} />
              <Typography variant="h3" fontWeight="bold" color="text.secondary">
                {healthData.summary.unknown || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">Unknown</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Plugin Update Section */}
      {pluginVersion && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SystemUpdateAlt sx={{ fontSize: 32, color: 'primary.main' }} />
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Plugin Update Manager
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Latest version: <Chip size="small" label={pluginVersion.version} color="primary" />
                  {pluginVersion.zip_exists ? (
                    <Chip size="small" label="ZIP Ready" color="success" sx={{ ml: 1 }} />
                  ) : (
                    <Chip size="small" label="ZIP Missing" color="error" sx={{ ml: 1 }} />
                  )}
                  {sitesNeedingUpdate > 0 && (
                    <Chip size="small" label={`${sitesNeedingUpdate} site(s) need update`} color="warning" sx={{ ml: 1 }} />
                  )}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="warning"
                startIcon={updating ? <CircularProgress size={20} color="inherit" /> : <Upload />}
                onClick={updateAllSites}
                disabled={updating || loading || !pluginVersion.zip_exists || sitesNeedingUpdate === 0}
              >
                {updating ? 'Updating...' : `Update All (${sitesNeedingUpdate})`}
              </Button>
            </Box>
          </Box>
          {updating && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress color="warning" />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Updating plugins... This may take several minutes.
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Actions */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          variant="contained"
          startIcon={checking ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
          onClick={checkAllSites}
          disabled={checking || loading}
          size="large"
        >
          {checking ? 'Checking All Sites...' : 'Check All Sites'}
        </Button>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => { fetchHealthStatus(); fetchPluginVersion(); }}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Status Filter */}
      <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
          Filter by status:
        </Typography>
        <Chip
          label={`All (${healthData.summary.total || 0})`}
          onClick={() => setStatusFilter('all')}
          color={statusFilter === 'all' ? 'primary' : 'default'}
          variant={statusFilter === 'all' ? 'filled' : 'outlined'}
        />
        <Chip
          icon={<CheckCircle />}
          label={`Healthy (${healthData.summary.healthy || 0})`}
          onClick={() => setStatusFilter('healthy')}
          color={statusFilter === 'healthy' ? 'success' : 'default'}
          variant={statusFilter === 'healthy' ? 'filled' : 'outlined'}
        />
        <Chip
          icon={<Warning />}
          label={`Degraded (${healthData.summary.degraded || 0})`}
          onClick={() => setStatusFilter('degraded')}
          color={statusFilter === 'degraded' ? 'warning' : 'default'}
          variant={statusFilter === 'degraded' ? 'filled' : 'outlined'}
        />
        <Chip
          icon={<Error />}
          label={`Offline (${healthData.summary.offline || 0})`}
          onClick={() => setStatusFilter('offline')}
          color={statusFilter === 'offline' ? 'error' : 'default'}
          variant={statusFilter === 'offline' ? 'filled' : 'outlined'}
        />
        <Chip
          icon={<HelpOutline />}
          label={`Unknown (${healthData.summary.unknown || 0})`}
          onClick={() => setStatusFilter('unknown')}
          color={statusFilter === 'unknown' ? 'default' : 'default'}
          variant={statusFilter === 'unknown' ? 'filled' : 'outlined'}
        />
      </Box>

      {checking && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Checking all sites... This may take a few minutes.
          </Typography>
        </Box>
      )}

      {/* Sites Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Site</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Plugin Version</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Plugin</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Forms</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Response Time</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Last Checked</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSites.map((site) => (
                <TableRow
                  key={site.id}
                  sx={{
                    '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                    '&:hover': { bgcolor: 'action.selected' }
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {site.rootdomain}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(site.status)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        size="small"
                        label={site.plugin_version || 'Unknown'}
                        variant="outlined"
                        color={needsUpdate(site.plugin_version) ? 'warning' : 'primary'}
                      />
                      {needsUpdate(site.plugin_version) && (
                        <Tooltip title={`Update available: ${pluginVersion?.version}`}>
                          <SystemUpdateAlt sx={{ fontSize: 16, color: 'warning.main' }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {site.plugin_reachable === true ? (
                      <Chip size="small" color="success" label="Connected" variant="outlined" />
                    ) : site.plugin_reachable === false ? (
                      <Tooltip title={site.plugin_error || 'Connection failed'}>
                        <Chip size="small" color="error" label="Disconnected" variant="outlined" />
                      </Tooltip>
                    ) : (
                      <Chip size="small" label="Unknown" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {site.forms_fetchable === true ? (
                      <Chip size="small" color="success" label="Working" variant="outlined" />
                    ) : site.forms_fetchable === false ? (
                      <Tooltip title={site.forms_error || 'Fetch failed'}>
                        <Chip size="small" color="error" label="Failed" variant="outlined" />
                      </Tooltip>
                    ) : (
                      <Chip size="small" label="N/A" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {site.plugin_response_time ? (
                      <Typography variant="body2">
                        {site.plugin_response_time}ms
                        {site.forms_response_time && ` / ${site.forms_response_time}ms`}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(site.last_health_check)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Check Health">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => checkSingleSite(site.id)}
                          disabled={checkingSiteId === site.id}
                        >
                          {checkingSiteId === site.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <PlayArrow />
                          )}
                        </IconButton>
                      </Tooltip>
                      {needsUpdate(site.plugin_version) && site.plugin_reachable && (
                        <Tooltip title="Update Plugin">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => updateSingleSite(site.id)}
                            disabled={updatingSiteId === site.id}
                          >
                            {updatingSiteId === site.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <SystemUpdateAlt />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Open Site">
                        <IconButton
                          size="small"
                          color="secondary"
                          href={site.website_url.startsWith('http') ? site.website_url : `https://${site.website_url}`}
                          target="_blank"
                        >
                          <OpenInNew />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {healthData.sites.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <HealthAndSafety sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No plugin-installed sites found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sites will appear here once they have the NKP Control Panel plugin installed
          </Typography>
        </Paper>
      )}

      {healthData.sites.length > 0 && filteredSites.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <HelpOutline sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No sites match the selected filter
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try selecting a different status filter or click "All" to see all sites
          </Typography>
          <Button
            variant="outlined"
            sx={{ mt: 2 }}
            onClick={() => setStatusFilter('all')}
          >
            Show All Sites
          </Button>
        </Paper>
      )}
    </Container>
  );
};

export default SiteHealth;
